import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { sendChatMessage, getChatbotHistory } from '../services/chatbot';
import { getUserRole, getUserId } from '../helpers/local_storage_helpers';
import CustomLoading from './customLoading/CustomLoading';
import { BiSend, BiMicrophone, BiMicrophoneOff, BiPlay, BiStop } from 'react-icons/bi';
import { IoMdSettings } from 'react-icons/io';
import Dropdown from './inputFields/DropDown';

const ChatbotComponent = ({ userId = null }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [language, setLanguage] = useState('en');
    const [showSettings, setShowSettings] = useState(true);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioRef = useRef(null);
    const recordingIntervalRef = useRef(null);
    const audioElementsRef = useRef({});

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        // Load chat history if userId is provided
        if (userId) {
            loadChatHistory();
        }
    }, [userId]);

    // Cleanup recording interval on unmount
    useEffect(() => {
        return () => {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    const loadChatHistory = async () => {
        try {
            setIsLoading(true);
            const response = await getChatbotHistory(userId);
            if (response.status === 200) {
                // Transform the history data to match our message format
                const historyMessages = response.data.map(item => ({
                    id: item.id,
                    text: item.message,
                    sender: 'user',
                    timestamp: item.timestamp,
                    session_id: null
                }));

                // Add bot responses
                const botMessages = response.data.map(item => ({
                    id: `${item.id}-response`,
                    text: item.response,
                    sender: 'bot',
                    timestamp: item.timestamp,
                    session_id: null,
                    voice_base64: item.voice_base64
                }));

                // Combine and sort by timestamp
                const allMessages = [...historyMessages, ...botMessages].sort((a, b) =>
                    new Date(a.timestamp) - new Date(b.timestamp)
                );

                setMessages(allMessages);
            }
        } catch (error) {
            console.error('Error loading chat history:', error);
            toast.error('Failed to load chat history');
        } finally {
            setIsLoading(false);
        }
    };

        const handleSendMessage = async () => {
        // If recording is in progress, stop it first and send the audio message
        if (isRecording) {
            stopRecording();
            // Wait a moment for the recording to stop and audio blob to be created
            setTimeout(() => {
                if (audioBlob) {
                    sendAudioMessage();
                }
            }, 100);
            return;
        }

        // If there's an audio blob, send audio message
        if (audioBlob) {
            sendAudioMessage();
            return;
        }

        // If no text message, don't proceed
        if (!inputMessage.trim()) return;

        const userMessageText = inputMessage.trim();
        const tempUserMessageId = `temp-user-${Date.now()}`;
        const tempBotMessageId = `temp-bot-${Date.now()}`;

        // Immediately show user message locally
        const localUserMessage = {
            id: tempUserMessageId,
            text: userMessageText,
            sender: 'user',
            timestamp: new Date().toISOString(),
            session_id: null
        };

        // Add user message immediately to UI
        setMessages(prev => [...prev, localUserMessage]);
        setInputMessage('');
        setIsLoading(true);

        // Add temporary "AI is thinking" message
        const tempBotMessage = {
            id: tempBotMessageId,
            text: '',
            sender: 'bot',
            timestamp: new Date().toISOString(),
            session_id: null,
            isTyping: true
        };

        setMessages(prev => [...prev, tempBotMessage]);

        const userMessage = {
            message: userMessageText,
            user_id: userId || getUserId(),
            language: language,
        };

        try {
            const response = await sendChatMessage(userMessage);

            if (response.status === 200) {
                // Remove temporary messages and reload complete history
                setMessages(prev => prev.filter(msg => 
                    msg.id !== tempUserMessageId && msg.id !== tempBotMessageId
                ));
                
                // Show success message
                toast.success('Message sent successfully');
                
                // Reload complete chat history to get the real messages with proper IDs
                await loadChatHistory();
            } else {
                // Remove temporary messages on error
                setMessages(prev => prev.filter(msg => 
                    msg.id !== tempUserMessageId && msg.id !== tempBotMessageId
                ));
                toast.error('Failed to get response from chatbot');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            
            // Remove temporary messages on error
            setMessages(prev => prev.filter(msg => 
                msg.id !== tempUserMessageId && msg.id !== tempBotMessageId
            ));
            
            toast.error('Failed to send message');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            const chunks = [];

            mediaRecorder.ondataavailable = (event) => {
                chunks.push(event.data);
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/wav' });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

            toast.success('Recording started');
        } catch (error) {
            console.error('Error starting recording:', error);
            toast.error('Failed to start recording. Please check microphone permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
                recordingIntervalRef.current = null;
            }

            toast.success('Recording stopped');
        }
    };

    const handleVoiceInput = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const playAudio = () => {
        if (audioRef.current) {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
    };

    const playMessageAudio = (messageId) => {
        // Stop any currently playing audio
        if (currentlyPlayingId && audioElementsRef.current[currentlyPlayingId]) {
            audioElementsRef.current[currentlyPlayingId].pause();
            audioElementsRef.current[currentlyPlayingId].currentTime = 0;
        }

        // Play the selected audio
        if (audioElementsRef.current[messageId]) {
            audioElementsRef.current[messageId].play();
            setCurrentlyPlayingId(messageId);
        }
    };

    const stopMessageAudio = (messageId) => {
        if (audioElementsRef.current[messageId]) {
            audioElementsRef.current[messageId].pause();
            audioElementsRef.current[messageId].currentTime = 0;
        }
        setCurrentlyPlayingId(null);
    };

    const handleMessageAudioEnded = (messageId) => {
        setCurrentlyPlayingId(null);
    };

    const handleMessageAudioPlay = (messageId) => {
        // Stop any other playing audio
        if (currentlyPlayingId && currentlyPlayingId !== messageId) {
            if (audioElementsRef.current[currentlyPlayingId]) {
                audioElementsRef.current[currentlyPlayingId].pause();
                audioElementsRef.current[currentlyPlayingId].currentTime = 0;
            }
        }
        setCurrentlyPlayingId(messageId);
    };

    const sendAudioMessage = async () => {
        if (!audioBlob) {
            toast.error('No audio recorded');
            return;
        }

        setIsLoading(true);
        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('voice', audioBlob, 'voice-message.wav');
            formData.append('user_id', userId || getUserId());
            formData.append('language', language);
            try {
                const response = await sendChatMessage(formData);

                if (response.status === 200) {
                    toast.info("Audio message sent successfully");
                    setAudioBlob(null);
                    setAudioUrl(null);
                    setRecordingTime(0);
                    loadChatHistory();
                } else {
                    toast.error('Failed to get response from chatbot');
                }
            } catch (error) {
                console.error('Error sending message:', error);
                toast.error('Failed to send message');
            } finally {
                setIsLoading(false);
            }

        } catch (error) {
            console.error('Error sending audio message:', error);
            toast.error('Failed to send audio message');
        } finally {
            setIsLoading(false);
        }
    };

    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Handle file upload logic
            console.log('File uploaded:', file);
        }
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatMessageText = (text) => {
        if (!text) return '';

        // Handle line breaks and paragraphs
        let formattedText = text
            .replace(/\n\n/g, '</p><p>') // Double line breaks become paragraphs
            .replace(/\n/g, '<br>'); // Single line breaks become <br>

        // Handle bold text (**text** or __text__)
        formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        formattedText = formattedText.replace(/__(.*?)__/g, '<strong>$1</strong>');

        // Handle italic text (*text* or _text_)
        formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        formattedText = formattedText.replace(/_(.*?)_/g, '<em>$1</em>');

        // Handle numbered lists (1. item)
        formattedText = formattedText.replace(/^(\d+\.\s+)(.*?)$/gm, '<li class="list-decimal">$1$2</li>');

        // Handle bullet points (- item or * item)
        formattedText = formattedText.replace(/^[-*]\s+(.*?)$/gm, '<li class="list-disc">$1</li>');

        // Wrap lists in <ul> or <ol> tags
        formattedText = formattedText.replace(/(<li class="list-decimal">.*?<\/li>)/gs, '<ol class="list-decimal ml-4">$1</ol>');
        formattedText = formattedText.replace(/(<li class="list-disc">.*?<\/li>)/gs, '<ul class="list-disc ml-4">$1</ul>');

        // Handle code blocks (```code```)
        formattedText = formattedText.replace(/```(.*?)```/gs, '<code class="bg-gray-200 px-2 py-1 rounded text-sm font-mono">$1</code>');

        // Handle inline code (`code`)
        formattedText = formattedText.replace(/`(.*?)`/g, '<code class="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">$1</code>');

        // Handle quotes (> text)
        formattedText = formattedText.replace(/^>\s+(.*?)$/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-600">$1</blockquote>');

        // Handle headers (# Header, ## Header, ### Header, #### Header)
        formattedText = formattedText.replace(/^####\s+(.*?)$/gm, '<h4 class="text-base font-bold mt-3 mb-2">$1</h4>');
        formattedText = formattedText.replace(/^###\s+(.*?)$/gm, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>');
        formattedText = formattedText.replace(/^##\s+(.*?)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>');
        formattedText = formattedText.replace(/^#\s+(.*?)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>');

        // Handle emphasis for important points
        formattedText = formattedText.replace(/⚠️\s*(.*?)(?=\n|$)/g, '<div class="bg-yellow-100 border-l-4 border-yellow-400 p-3 my-2"><span class="text-yellow-800">⚠️ $1</span></div>');
        formattedText = formattedText.replace(/💡\s*(.*?)(?=\n|$)/g, '<div class="bg-blue-100 border-l-4 border-blue-400 p-3 my-2"><span class="text-blue-800">💡 $1</span></div>');
        formattedText = formattedText.replace(/✅\s*(.*?)(?=\n|$)/g, '<div class="bg-green-100 border-l-4 border-green-400 p-3 my-2"><span class="text-green-800">✅ $1</span></div>');

        // Wrap in paragraph tags if not already wrapped
        if (!formattedText.startsWith('<')) {
            formattedText = `<p>${formattedText}</p>`;
        }

        return formattedText;
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
            <style jsx>{`
                .prose {
                    color: inherit;
                }
                .prose strong {
                    font-weight: 600;
                }
                .prose em {
                    font-style: italic;
                }
                .prose code {
                    background-color: rgba(0, 0, 0, 0.1);
                    padding: 0.125rem 0.25rem;
                    border-radius: 0.25rem;
                    font-family: monospace;
                    font-size: 0.875rem;
                }
                .prose blockquote {
                    border-left: 4px solid #d1d5db;
                    padding-left: 1rem;
                    font-style: italic;
                    color: #6b7280;
                    margin: 1rem 0;
                }
                .prose ul, .prose ol {
                    margin: 0.5rem 0;
                    padding-left: 1.5rem;
                }
                .prose li {
                    margin: 0.25rem 0;
                }
                .prose h1, .prose h2, .prose h3 {
                    margin: 1rem 0 0.5rem 0;
                    font-weight: 600;
                }
                .prose p {
                    margin: 0.5rem 0;
                }
            `}</style>
            {/* Enhanced Header */}
            <div className="flex items-center justify-between p-6 border-b border-primary/20 bg-gradient-to-r from-primary to-green-600 text-white">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-primary font-bold text-xl">🤖</span>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Fasalam AI Assistant</h3>
                        <p className="text-sm opacity-90 flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            Online & Ready to Help
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                        <span className="text-xs font-medium">{language === 'en' ? '🇺🇸 EN' : language === 'hi' ? '🇮🇳 HI' : '🇮🇳 GU'}</span>
                    </div>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="p-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title="Settings"
                    >
                        <IoMdSettings size={20} />
                    </button>
                </div>
            </div>

            {/* Enhanced Settings Panel */}
            {showSettings && (
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-primary/10 to-green-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                                <IoMdSettings className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800">Chat Settings</h4>
                                <p className="text-xs text-gray-600">Customize your AI experience</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-semibold text-gray-700">🌐 Language:</label>
                            <div className="min-w-[140px]">
                                <Dropdown
                                    options={[
                                        { label: '🇺🇸 English', value: 'en' },
                                        { label: '🇮🇳 Hindi', value: 'hi' },
                                        { label: '🇮🇳 Gujarati', value: 'gu' }
                                    ]}
                                    value={language}
                                    onChange={(e) => setLanguage(e)}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoading && messages.length === 0 && (
                    <div className="flex justify-center">
                        <CustomLoading size={40} />
                    </div>
                )}

                {!isLoading && messages.length === 0 && (
                    <div className="flex justify-center">
                        <div className="text-center text-gray-500">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🌾</span>
                            </div>
                            <h3 className="text-lg font-medium mb-2">Welcome to Agricultural Assistant</h3>
                            <p className="text-sm">Ask me anything about farming, crop management, weather, or agricultural practices.</p>
                            <p className="text-xs mt-2 text-gray-400">I can help in English, Hindi, and Gujarati</p>
                        </div>
                    </div>
                )}

                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} ${message.id.startsWith('temp-') ? 'animate-in slide-in-from-bottom-2 duration-200' : ''
                            }`}
                    >
                        <div
                            className={`max-w-xs lg:max-w-md xl:max-w-lg px-4 py-3 rounded-lg ${message.sender === 'user'
                                ? 'bg-primary text-white shadow-md'
                                : message.isTyping
                                    ? 'bg-gray-100 border-2 border-gray-200 animate-pulse'
                                    : 'bg-gray-100 text-gray-800 shadow-sm'
                                } ${message.sender === 'bot' ? 'prose prose-sm max-w-none' : ''}`}
                        >
                            {message.isTyping ? (
                                <div className="flex items-center space-x-2 py-2">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                    <span className="text-xs text-gray-500 ml-2">AI is thinking...</span>
                                </div>
                            ) : message.voice_base64 ? (
                                <div className="flex flex-col items-start">
                                    <div className="flex flex-col w-full my-2">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="text-xs text-gray-500">🔊 Voice Response</span>
                                            <button
                                                className={`px-2 py-1 text-xs rounded transition ${currentlyPlayingId === message.id
                                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                    : 'bg-primary/20 text-primary hover:bg-primary/30'
                                                    }`}
                                                onClick={() => {
                                                    if (currentlyPlayingId === message.id) {
                                                        stopMessageAudio(message.id);
                                                    } else {
                                                        playMessageAudio(message.id);
                                                    }
                                                }}
                                                title={currentlyPlayingId === message.id ? "Stop" : "Play from start"}
                                            >
                                                {currentlyPlayingId === message.id ? 'Stop' : 'Play'}
                                            </button>
                                            <a
                                                href={message.voice_base64}
                                                download={`chatbot-voice-${message.id}.wav`}
                                                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition"
                                                title="Download audio"
                                            >
                                                Download
                                            </a>
                                        </div>
                                        <audio
                                            ref={(el) => {
                                                if (el) {
                                                    audioElementsRef.current[message.id] = el;
                                                }
                                            }}
                                            src={message.voice_base64}
                                            onPlay={() => handleMessageAudioPlay(message.id)}
                                            onEnded={() => handleMessageAudioEnded(message.id)}
                                            onPause={() => {
                                                if (currentlyPlayingId === message.id) {
                                                    setCurrentlyPlayingId(null);
                                                }
                                            }}
                                            className="w-full rounded shadow"
                                            style={{ background: "#f9fafb" }}
                                            controls
                                        >
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: formatMessageText(message.text)
                                    }}
                                />
                            )}
                            {!message.isTyping && (
                                <p className={`text-xs mt-2 ${message.sender === 'user' ? 'text-white opacity-70' : 'text-gray-500'
                                    }`}>
                                    {formatTime(message.timestamp)}
                                    {message.id.startsWith('temp-') && (
                                        <span className="ml-2 opacity-60">⏳</span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Enhanced Input Area */}
            <div className="p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-primary/10">
                <div className="flex items-center gap-3">
                    {/* Voice Input Button */}
                    <button
                        onClick={handleVoiceInput}
                        className={`p-3 rounded-lg transition-all duration-200 shadow-md ${isRecording
                            ? 'text-red-500 bg-red-100 hover:bg-red-200 ring-2 ring-red-300'
                            : 'text-primary bg-white hover:bg-primary/10 hover:text-primary border border-primary/30'
                            }`}
                        title={isRecording ? 'Stop recording' : 'Voice input'}
                        aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
                    >
                        {isRecording ? <BiMicrophoneOff size={22} /> : <BiMicrophone size={22} />}
                    </button>

                    {/* Input Field Area */}
                    <div className="flex-1">
                        {isRecording ? (
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                        <span className="text-sm font-bold text-red-800">
                                            🎙️ Recording... {formatRecordingTime(recordingTime)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                            💡 Click Send to stop & send
                                        </div>
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
                                    </div>
                                </div>
                            </div>
                        ) : audioBlob ? (
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold text-blue-900">🎵 Audio Message</span>
                                        <span className="text-xs text-blue-700 bg-blue-100 px-3 py-1 rounded-full font-medium">
                                            {formatRecordingTime(recordingTime)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={isPlaying ? stopAudio : playAudio}
                                            className="p-2 rounded-lg text-blue-700 hover:bg-blue-100 transition-colors border border-blue-300"
                                            title={isPlaying ? 'Stop' : 'Play'}
                                            aria-label={isPlaying ? 'Stop audio playback' : 'Play audio message'}
                                        >
                                            {isPlaying ? <BiStop size={18} /> : <BiPlay size={18} />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setAudioBlob(null);
                                                setAudioUrl(null);
                                                setRecordingTime(0);
                                            }}
                                            className="p-2 rounded-lg text-red-600 hover:bg-red-100 transition-colors border border-red-300"
                                            title="Delete"
                                            aria-label="Delete audio message"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <audio
                                    ref={audioRef}
                                    src={audioUrl}
                                    onEnded={handleAudioEnded}
                                    className="hidden"
                                />
                            </div>
                        ) : (
                            <div className="relative">
                                <textarea
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="💬 Type your message here... (Press Enter to send, Shift+Enter for new line)"
                                    className="w-full px-4 py-3 border border-primary/30 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 text-sm bg-white shadow-sm"
                                    rows="1"
                                    style={{ minHeight: '48px', maxHeight: '120px' }}
                                    aria-label="Type your message"
                                />
                                <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                                    {inputMessage.length > 0 && `${inputMessage.length} chars`}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Enhanced Send Button */}
                    <button
                        onClick={handleSendMessage}
                        disabled={(!inputMessage.trim() && !audioBlob && !isRecording) || isLoading}
                        className={`p-3 rounded-lg shadow-md transition-all duration-200 ${
                            (!inputMessage.trim() && !audioBlob && !isRecording) || isLoading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-primary hover:bg-primary-dark text-white hover:shadow-lg transform hover:scale-105'
                        }`}
                        title={
                            isRecording ? "Stop recording & send audio" :
                            audioBlob ? "Send audio message" : 
                            "Send message"
                        }
                        aria-label={
                            isRecording ? "Stop recording and send audio" :
                            audioBlob ? "Send audio message" : 
                            "Send message"
                        }
                    >
                        {isLoading ? (
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : isRecording ? (
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                <BiSend size={18} />
                            </div>
                        ) : (
                            <BiSend size={22} />
                        )}
                    </button>
                </div>

                {/*
                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="audio/*,image/*,.pdf,.doc,.docx"
                />
                */}
            </div>
        </div>
    );
};

export default ChatbotComponent; 